import { useCallback } from 'react';
import { CREATE, GET_ONE, showNotification, UPDATE } from 'react-admin';

const downloadPdf = (data, { fileName }) => {
  const newBlob = new Blob([data]);

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const objectUrl = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();

  // For Firefox it is necessary to delay revoking the ObjectURL
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(data);
  }, 100);
};

export const useUnitInvoiceActions = ({ dataProvider, selectedInvoiceId, dispatch }) => {
  const retryPayment = useCallback(
    async (invoiceId, data) => {
      try {
        await dataProvider(CREATE, 'businessUnitInvoices', {
          field: 'retryPayment',
          id: invoiceId,
          data,
        });
        dispatch(showNotification('notification.invoices.setToPendingSuccessfully'));
      } catch (error) {
        console.error(`Failed to retry payment for invoice ${invoiceId}`, error);
        dispatch(
          showNotification('notification.invoices.setToPendingFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch]
  );

  const refund = useCallback(
    async (invoiceId, payload, data, updateRefundDialogView) => {
      try {
        await dataProvider(CREATE, 'businessUnitInvoices', { field: 'refund', id: invoiceId, payload, data });
        updateRefundDialogView();
        dispatch(showNotification('notification.invoices.refundedSuccessfully'));
      } catch (error) {
        console.error(`Failed to refund invoice ${invoiceId}`, error);
        dispatch(
          showNotification('notification.invoices.refundedFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch]
  );

  const downloadRefundNote = useCallback(
    (paymentRefundPspReference, invoiceId) => {
      dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', {
        field: 'refundNote',
        id: selectedInvoiceId || invoiceId,
        paymentRefundPspReference,
      })
        .then(({ data }) => downloadPdf(data, { fileName: 'refund' }))
        .catch(error => {
          console.error(`Failed to download refund note for invoice ${selectedInvoiceId || invoiceId}`, error);
          dispatch(
            showNotification('notification.invoices.refundNoteFailure', 'warning', {
              messageArgs: { error_message: error },
            })
          );
        });
    },
    [dataProvider, dispatch, selectedInvoiceId]
  );

  const downloadInvoicePdf = useCallback(
    invoiceId => {
      dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', { field: 'invoicePdf', id: invoiceId })
        .then(({ data }) => downloadPdf(data, { fileName: 'invoice' }))
        .catch(error => {
          console.error(`Failed to download invoice pdf for invoice ${invoiceId}`, error);
          dispatch(
            showNotification('notification.invoices.downloadInvoiceFailure', 'warning', {
              messageArgs: { error_message: error },
            })
          );
        });
    },
    [dataProvider, dispatch]
  );

  const cancel = useCallback(
    async (invoiceId, data) => {
      try {
        await dataProvider(UPDATE, 'businessUnitInvoices', { field: 'cancel', id: invoiceId, data });
        dispatch(showNotification('notification.invoices.canceledSuccessfully'));
      } catch (error) {
        console.error(`Failed to cancel invoice ${invoiceId}`, error);
        dispatch(
          showNotification('notification.invoices.canceledFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch]
  );

  const setAsPaid = useCallback(
    async (invoiceId, payload, data) => {
      try {
        await dataProvider(UPDATE, 'businessUnitInvoices', { field: 'setAsPaid', id: invoiceId, payload, data });
        dispatch(showNotification('notification.invoices.paidSuccessfully'));
      } catch (error) {
        console.error(`Failed to set invoice ${invoiceId} as paid`, error);
        dispatch(
          showNotification('notification.invoices.paidFailure', 'warning', { messageArgs: { error_message: error } })
        );
      }
    },
    [dataProvider, dispatch]
  );

  return {
    retryPayment,
    refund,
    downloadRefundNote,
    downloadInvoicePdf,
    cancel,
    setAsPaid,
  };
};
